// @givmo/app-charity — the console's route literals, in ONE place (CPA-WEB40).
//
// The landing rule, the class table, the shell's declarations, the not-found control and the router
// all reach for these strings. Eight files typing the same literal is how one of them ends up
// pointing at a path that moved — the company console keeps its own `router/console-routes.ts`
// for the same reason.
//
// ⚠ `/` IS NOT ON THIS LIST, AND ITS ABSENCE IS THE CONTRACT. The root does not render: it computes
// where this person belongs and replaces itself (`router/console-landing.tsx`). A place-named
// control pointing at it would bounce whoever pressed it somewhere the control did not name. The
// Givmo mark and the not-found control carry `/` deliberately — they name the person's LANDING,
// not a place, and compute it on click.
//
// ⚠ THE RETIRED PATHS STAY ADDRESSABLE AS REPLACE-REDIRECTS. Links and bookmarks outlive a re-home,
// and the BLOCKING e2e attribution spec still opens `/grants` today (CPA-E2E13 retargets it in the
// same round). A pushed redirect would leave a back-button trap, so every one of them replaces.

/**
 * `/apps` — the landing rule's last resort (shell contract v3, D3/D6): the "your apps are in the
 * menu" statement page for a person with nothing openable, and a replace-redirect onto the Admin
 * Console's Apps page for a holder of `write_settings`. NOT a home: no control names it as a place
 * — the Givmo mark and the not-found control compute the person's landing instead.
 */
export const APPS_ROUTE = '/apps';

/**
 * The Admin Console's Apps page — the organization's own management pages as a tile (`/org`
 * family: Apps · Team access · Activity), where a switched-off tile's "Turn on" affordance opens.
 */
export const ORG_ROUTE = '/org';

/** The Admin Console's Team access page: the roster, the invites, the pending requests, the connected AIs. */
export const TEAM_ACCESS_ROUTE = '/org/team-access';

/** The Admin Console's Activity page: the access event log, its own page (shell contract v3). */
export const ACTIVITY_ROUTE = '/org/activity';

/**
 * The gear's ONE composed settings page (shell contract v3, T2 as amended at D-CPO-33; Org Machinery
 * OM-17 design 3). ⚠ `/settings` ON BOTH HOSTS, IN ONE MOVE (the OM-17 cut-over, RULED 2026-09-07):
 * the company console keeps the SAME literal in its `router/console-routes.ts`, and
 * `@givmo/org-console`'s `settings-address.test.ts` refuses the two drifting apart. The literal is
 * spelled here and nowhere else in this app. The catalog-removal request page that held `/settings`
 * (CPA-WEB7) answers `/removal` now (`features/settings/settings-routes.ts`), titled "Remove from
 * Givmo".
 */
export const SETTINGS_ROUTE = '/settings';

/**
 * The Donations module's landing: the certified statement, renamed Donations (§2 ruling 2, A7).
 * The module's whole route family hangs under it — the legacy drill-down, Payments and the free
 * Team page included — so the sidebar rule reads every one of them as inside the module.
 */
export const DONATIONS_ROUTE = '/donations';

/** Payments — the module's secondary page (§9-9). */
export const DONATIONS_PAYMENTS_ROUTE = '/donations/payments';

/** The legacy per-grant drill-down's new address (the V2-OFF arm; V2 redirects into the table). */
export const DONATIONS_GRANT_DETAIL_PATH = '/donations/grants/$grantId';

/**
 * The organization deep link's family root (CPA-BE29) — the shell's page-title declaration matches
 * on it (`console-nav.ts`); the route itself is {@link ORGANIZATION_DEEP_LINK_PATH}.
 */
export const ORGANIZATIONS_ROUTE = '/organizations';

/**
 * The organization deep link — the ONE address that opens a NAMED organization's console
 * (CPA-BE29; `router/organization-deep-link.tsx`). The active organization otherwise comes from
 * per-user storage alone, so before this route nothing a mail could point at landed a claimant
 * INSIDE the approved charity's console; the claim-approved email points here.
 *
 * ⚠ MIRRORED BY THE BACKEND, LITERALLY: `givmo-backend-python` builds the email's destination from
 * this same path (`app/config.py`, `CHARITY_PORTAL_ORGANIZATION_PATH`, read at a pinned SHA of this
 * file) and a test on each side pins the literal. Change it here and there in one round, never one.
 * Not `/claim/$charityId` (the claim wizard) and not inside the `/org` Admin Console family.
 */
export const ORGANIZATION_DEEP_LINK_PATH = `${ORGANIZATIONS_ROUTE}/$charityId`;

/**
 * The deep link's DESTINATION, a search param (CCM-R1-SIGN-LINK-ORG): the console page the link
 * opens once the named organization is selected — `/organizations/<id>?to=/compliance/sign/<request
 * id>` is what a signing email names, so a person whose active organization is another one lands
 * on the request in ITS organization rather than meeting "Not found". Honoured only for an
 * organization the person holds, and only for a destination `organizationDeepLinkDestination`
 * admits (`router/organization-deep-link.tsx`); without one, the organization's landing, as before.
 *
 * ⚠ MIRRORED BY THE BACKEND, LITERALLY: `app/config.py` `CHARITY_PORTAL_ORGANIZATION_DESTINATION_PARAM`,
 * which builds the emails' links; a test on each side pins it. Change both in one round, never one.
 */
export const ORGANIZATION_DEEP_LINK_DESTINATION_PARAM = 'to';

/** Build the organization deep link for a concrete charity id, with the page it should open there. */
export function organizationDeepLinkPath(charityId: string, destination?: string): string {
  const path = `${ORGANIZATIONS_ROUTE}/${encodeURIComponent(charityId)}`;
  return destination === undefined
    ? path
    : `${path}?${ORGANIZATION_DEEP_LINK_DESTINATION_PARAM}=${encodeURIComponent(destination)}`;
}

/** The retired paths — replace-redirects into the console. */
export const RETIRED_DASHBOARD_ROUTE = '/dashboard';
export const RETIRED_GRANTS_ROUTE = '/grants';
export const RETIRED_GRANT_DETAIL_PATH = '/grants/$grantId';
export const RETIRED_PAYMENT_ROUTE = '/payment';

/**
 * RETIRED — the CBO-CT1 slice-6 team-access path, a replace-redirect to `/org/team-access` with its
 * search preserved (`?module=`). The build-time knob that gated it retired with the move.
 */
export const RETIRED_TEAM_ACCESS_ROUTE = '/team-access';

/**
 * RETIRED — the composed settings page's address before the OM-17 cut-over (the OM-9 build's
 * `/preferences`), a replace-redirect to `SETTINGS_ROUTE`: the address is in people's history and in
 * the OM-14 e2e specs.
 */
export const RETIRED_PREFERENCES_ROUTE = '/preferences';

/**
 * RETIRED — the Phase-2 Compliance surface's six addresses (CCM-WEB1/2), replace-redirects into the
 * module's `/compliance*` family since CCM-MOD1B. Each carries its search AND its hash across, and
 * the drill-down its `$obligationId` verbatim; all six exist only while the module's knob mounts
 * it, so a dark build still answers not-found at every one of them (the BLOCKING e2e spec's pin).
 */
export const RETIRED_FILING_REGISTER_ROUTE = '/filing-register';
export const RETIRED_FILING_REGISTER_REVIEW_ROUTE = '/filing-register/review';
export const RETIRED_FILING_REGISTER_DEADLINES_ROUTE = '/filing-register/deadlines';
export const RETIRED_FILING_REGISTER_FACTS_ROUTE = '/filing-register/recorded-facts';
export const RETIRED_FILING_REGISTER_DOCUMENTS_ROUTE = '/filing-register/documents';
export const RETIRED_FILING_REGISTER_OBLIGATION_PATH = '/filing-register/$obligationId';
